import React, { useEffect, useState } from "react"
import axios from "axios"
import { toast } from "sonner"
import { Card } from "../../components/ui/card"
import CategoryFilter from "../../components/CategoryFilter"
import { ExternalLink, Clock, TrendingUp } from "lucide-react"

const NEWS_API_URL = "https://openapiv1.coinstats.app/news"
// Support both env names and the provided key to avoid missing header issues
const COINSTATS_API_KEY =
  process.env.COINSTATS_API_KEY ||
  process.env.COINSTAT_API ||
  process.env.REACT_APP_COINSTAT_API ||
  "EEHFwhKJhaslm/RuYG9EqfOmQXwmKemD3CTtiGMRt58="
const FALLBACK_IMAGE =
  "https://via.placeholder.com/400x200/111827/FFFFFF?text=Crypto+News"

const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const minutes = Math.floor(diffMs / (1000 * 60))
  if (minutes < 60) return `${minutes || 1} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr${hours > 1 ? "s" : ""} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days > 1 ? "s" : ""} ago`
}

const resolveCategory = (title = "", coins = []) => {
  const normalizedTitle = title.toLowerCase()
  if (coins.includes("BTC")) return "Bitcoin"
  if (coins.includes("ETH")) return "Ethereum"
  if (normalizedTitle.includes("defi")) return "DeFi"
  if (normalizedTitle.includes("nft")) return "NFTs"
  if (normalizedTitle.includes("regulation")) return "Regulation"
  return "Markets"
}

const fallbackArticles = [
  {
    id: 1,
    title: "Bitcoin Reaches New All-Time High of $90,000",
    excerpt:
      "Bitcoin has surged to a new record high, breaking through the $90,000 barrier amid strong institutional demand and positive regulatory developments.",
    image: "https://via.placeholder.com/400x200/F7931A/FFFFFF?text=Bitcoin",
    category: "Bitcoin",
    source: "CryptoNews",
    time: "2 hours ago",
    trending: true,
    link: "#",
  },
  {
    id: 2,
    title: "Ethereum 2.0 Upgrade Shows Promising Results",
    excerpt:
      "The latest Ethereum upgrade has significantly improved transaction speeds and reduced gas fees, marking a major milestone for the network.",
    image: "https://via.placeholder.com/400x200/627EEA/FFFFFF?text=Ethereum",
    category: "Ethereum",
    source: "ETH News",
    time: "5 hours ago",
    trending: false,
    link: "#",
  },
  {
    id: 3,
    title: "DeFi TVL Surpasses $200 Billion Milestone",
    excerpt:
      "Total Value Locked in DeFi protocols has exceeded $200 billion, signaling growing confidence in decentralized finance applications.",
    image: "https://via.placeholder.com/400x200/10B981/FFFFFF?text=DeFi",
    category: "DeFi",
    source: "DeFi Daily",
    time: "1 day ago",
    trending: true,
    link: "#",
  },
  {
    id: 4,
    title: "Major Banks Announce Crypto Custody Services",
    excerpt:
      "Leading financial institutions are launching cryptocurrency custody solutions, making it easier for institutional investors to hold digital assets.",
    image: "https://via.placeholder.com/400x200/4169E1/FFFFFF?text=Banking",
    category: "Markets",
    source: "Financial Times",
    time: "1 day ago",
    trending: false,
    link: "#",
  },
  {
    id: 5,
    title: "NFT Market Shows Signs of Recovery",
    excerpt:
      "NFT trading volumes have increased by 45% this month, with blue-chip collections leading the revival.",
    image: "https://via.placeholder.com/400x200/A855F7/FFFFFF?text=NFTs",
    category: "NFTs",
    source: "NFT Today",
    time: "2 days ago",
    trending: false,
    link: "#",
  },
  {
    id: 6,
    title: "New Crypto Regulations Proposed in Major Markets",
    excerpt:
      "Governments worldwide are working on comprehensive cryptocurrency regulations to protect investors while fostering innovation.",
    image: "https://via.placeholder.com/400x200/EF4444/FFFFFF?text=Regulation",
    category: "Regulation",
    source: "Regulatory Watch",
    time: "3 days ago",
    trending: false,
    link: "#",
  },
]

const NewsNew = () => {
  const [activeCategory, setActiveCategory] = useState("All")
  const [newsArticles, setNewsArticles] = useState([])
  const [loading, setLoading] = useState(true)

  const categories = [
    "All",
    "Bitcoin",
    "Ethereum",
    "DeFi",
    "NFTs",
    "Regulation",
    "Markets",
  ]

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const { data } = await axios.get(NEWS_API_URL, {
          headers: {
            "X-API-KEY": COINSTATS_API_KEY,
          },
          // CoinStats news DTO throws if skip is provided; limit is accepted
          params: { limit: 40 },
        })
        const items = data?.news || data?.result || []
        const articles = items.map((item, index) => {
          const category = resolveCategory(item.title, item.coins || [])
          return {
            id: item.id || item._id || item.guid || index,
            title: item.title,
            excerpt: item.description || item.content || "",
            image: item.imgUrl || item.image_url || FALLBACK_IMAGE,
            category,
            source: item.source || item.feed || "CoinStats",
            time: formatTimeAgo(
              item.feedDate || item.published_at || Date.now()
            ),
            trending:
              typeof item.reactionsCount === "number"
                ? item.reactionsCount > 20
                : index < 3,
            link: item.link || item.url || "#",
          }
        })

        setNewsArticles(articles.length ? articles : fallbackArticles)
      } catch (error) {
        console.error("News fetch error:", error)
        toast.error("Unable to load crypto news right now.")
        setNewsArticles(fallbackArticles)
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  const filteredNews =
    activeCategory === "All"
      ? newsArticles
      : newsArticles.filter((article) => article.category === activeCategory)

  // Split news into main stories (left) and featured news (right)
  const mainStories = filteredNews.slice(
    0,
    Math.ceil(filteredNews.length * 0.6)
  )
  const featuredNews = filteredNews.slice(Math.ceil(filteredNews.length * 0.6))

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #062136;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
        `}</style>
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="news-page"
    >
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Crypto News
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm md:text-base">
          Stay updated with the latest cryptocurrency news
        </p>
      </div>

      {/* Category Filters */}
      <CategoryFilter
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      {/* Main Grid Layout: 3 columns for news, 1 for featured */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Stories: 3-column grid */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {mainStories.length > 0 ? (
              mainStories.map((article) => (
                <Card
                  key={article.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex flex-col h-full"
                >
                  <div className="relative">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-48 object-cover"
                    />
                    {article.trending && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-full flex items-center">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        Trending
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-medium rounded">
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {article.time}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {article.source}
                      </span>
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium flex items-center"
                      >
                        Read More
                        <ExternalLink className="w-4 h-4 ml-1" />
                      </a>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="p-12 text-center col-span-full">
                <p className="text-gray-500 dark:text-gray-400">
                  No news articles found in this category.
                </p>
              </Card>
            )}
          </div>
        </div>

        {/* Featured News Sidebar */}
        <div className="lg:col-span-4 space-y-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Featured News
          </h2>
          {featuredNews.length > 0 ? (
            featuredNews.map((article) => (
              <Card
                key={article.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex gap-3 p-4">
                  <div className="flex-shrink-0">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1 block">
                      {article.category}
                    </span>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {article.title}
                    </h4>
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <span>{article.source}</span>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {article.time}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-6 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No featured news available.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewsNew
